import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  RiArrowDownLine,
  RiArrowDownSLine,
  RiArrowUpLine,
  RiTeamLine,
  RiCloseLine,
  RiMailLine,
  RiMapPinLine,
  RiSaveLine,
  RiListCheck3,
} from '@remixicon/react';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@store/authStore';
import { ROUTES } from '@/constants';
import { COUNTRY_CODES } from '@/utils/countryCode';
import PhoneInput from '@/components/forms/PhoneInput';


const ENTITY_TYPES = [
  { value: '', label: 'Select entity type...' },
  { value: 'individual', label: 'Individual' },
  { value: 'company', label: 'Company' },
  { value: 'family', label: 'Family' },
  { value: 'agency', label: 'Agency' },
];

const parsePhoneNumber = (fullPhone) => {
  if (!fullPhone) return { countryCode: '+1', phone: '' };
  const match = COUNTRY_CODES.find((c) => fullPhone.startsWith(c.code));
  if (match) return { countryCode: match.code, phone: fullPhone.slice(match.code.length) };
  return { countryCode: '+1', phone: fullPhone };
};

const AddClient = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { userProfile } = useAuthStore();
  const isEditMode = !!id;
  const initialClient = location.state?.client || null;

  const { data: fetchedClient } = useQuery({
    queryKey: ['client', id],
    queryFn: () => apiClient.getClientById(id),
    enabled: isEditMode && !initialClient,
  });

  const client = initialClient || fetchedClient;

  const [activeSection, setActiveSection] = useState('details');
  const [entityTypeOpen, setEntityTypeOpen] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);

  const [form, setForm] = useState({
    entityType: '',
    entityName: '',
    firstName: '',
    lastName: '',
    phone: '',
    countryCode: '+1',
    email: '',
    address1: '',
    city: '',
    state: '',
    zip_code: '',
    operationalPreferences: '',
  });

  // Pre-populate form in edit mode
  useEffect(() => {
    if (isEditMode && client && !formInitialized) {
      const rawPhone = client.contact_numbers?.[0] || client.phone || '';
      const { countryCode, phone } = parsePhoneNumber(rawPhone);
      setForm({
        entityType: client.entity_type || '',
        entityName: client.company_name || client.company || '',
        firstName: client.first_name || '',
        lastName: client.last_name || '',
        phone,
        countryCode,
        email: client.email || '',
        address1: client.address1 || '',
        city: client.city || '',
        state: client.state || '',
        zip_code: client.zip_code || '',
        operationalPreferences: client.operational_preferences || '',
      });
      setFormInitialized(true);
    }
  }, [isEditMode, client, formInitialized]);

  const [errors, setErrors] = useState({});

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateDetails = () => {
    const newErrors = {};
    //if (!form.entityType) newErrors.entityType = 'Entity type is required';
    //if (!form.entityName.trim()) newErrors.entityName = 'Entity name is required';
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!form.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!form.email.trim()) newErrors.email = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Invalid email address';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateDetails()) {
      setActiveSection('preferences');
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        company_name: form.entityName.trim(),
        entity_type: form.entityType,
        email: form.email.trim(),
        contact_numbers: form.phone ? [`${form.countryCode}${form.phone}`] : [],
        address1: form.address1.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        zip_code: form.zip_code.trim() || null,
        broker_id: userProfile?.userprofile_id || '',
        operational_preferences: form.operationalPreferences || null,
      };

      if (isEditMode) {
        return apiClient.updateClient(id, payload);
      }
      return apiClient.createClient(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['client', id] });
        toast.success('Client updated successfully!');
        navigate(-1);
      } else {
        toast.success('Client created successfully!');
        navigate(ROUTES.CLIENTS.path);
      }
    },
    onError: (err) => {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} client:`, err);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} client. Please try again.`);
    },
  });

  const isDetailsComplete = form.entityType && form.entityName.trim() && form.firstName.trim() && form.lastName.trim() && form.phone.trim() && form.email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const handleSubmit = () => {
    if (!validateDetails()) {
      setActiveSection('details');
      return;
    }
    saveMutation.mutate();
  };

  return (
    <div className="pb-8 max-w-2xl space-y-4">
      {/* Client Details Section */}
      <div className="bg-white rounded-2xl overflow-hidden">
        <button
          onClick={() => setActiveSection(activeSection === 'details' ? '' : 'details')}
          className="w-full flex items-center gap-3 p-5 text-left"
        >
          <RiTeamLine className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-900">Client Details</span>
        </button>

        {activeSection === 'details' && (
          <div className="px-5 pb-5 space-y-5">
            {/* Entity Type */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Entity Type 
                {/* <span className="text-red-500">*</span> */}
              </label>
              <button
                type="button"
                onClick={() => setEntityTypeOpen(!entityTypeOpen)}
                className={`w-full flex items-center justify-between px-4 py-2.5 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.entityType ? 'border-red-500' : 'border-gray-200'
                }`}
              >
                <span className={form.entityType ? 'text-gray-900' : 'text-gray-400'}>
                  {ENTITY_TYPES.find((t) => t.value === form.entityType)?.label || 'Select entity type...'}
                </span>
                <RiArrowDownSLine className="w-4 h-4 text-gray-400" />
              </button>
              {errors.entityType && <p className="text-xs text-red-500 mt-1">{errors.entityType}</p>}

              {/* Popup modal via portal */}
              {entityTypeOpen && createPortal(
                <>
                  <div className="fixed inset-0 z-[9998] bg-black/30" onClick={() => setEntityTypeOpen(false)} />
                  <div className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-3xl shadow-xl overflow-hidden">
                    <div className="flex items-center justify-between px-5 pt-4 pb-2">
                      <h3 className="text-base font-semibold text-primary-700">Entity Type</h3>
                      <button
                        type="button"
                        onClick={() => setEntityTypeOpen(false)}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <RiCloseLine className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="pb-3">
                      {ENTITY_TYPES.filter((t) => t.value).map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => {
                            updateField('entityType', t.value);
                            setEntityTypeOpen(false);
                          }}
                          className={`w-full text-left px-5 py-2.5 text-sm transition-colors hover:bg-gray-50 ${
                            form.entityType === t.value ? 'text-primary-600 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>,
                document.body
              )}
            </div>

            {/* Entity Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Entity Name
                {/* <span className="text-red-500">*</span> */}
              </label>
              <input
                type="text"
                value={form.entityName}
                onChange={(e) => updateField('entityName', e.target.value)}
                placeholder="Enter entity name..."
                className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.entityName ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <p className="text-xs text-gray-400 mt-1">Company name, family name, or agency name.</p>
              {errors.entityName && <p className="text-xs text-red-500 mt-1">{errors.entityName}</p>}
            </div>

            {/* First Name & Last Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  First Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => updateField('firstName', e.target.value)}
                  placeholder="First name..."
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.firstName ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
                {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Last Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={(e) => updateField('lastName', e.target.value)}
                  placeholder="Last name..."
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.lastName ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
                {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
              </div>
            </div>

            {/* Phone Number */}
            <PhoneInput
              countryCode={form.countryCode}
              phone={form.phone}
              onCountryCodeChange={(code) => updateField('countryCode', code)}
              onPhoneChange={(phone) => updateField('phone', phone)}
              error={errors.phone}
              required
            />

            {/* Email Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <RiMailLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="example@email.com"
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
              </div>
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Address
              </label>
              <div className="relative">
                <RiMapPinLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={form.address1}
                  onChange={(e) => updateField('address1', e.target.value)}
                  placeholder="123 Main Street, Apt 4B"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* City, State, Zip */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  City
                </label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  placeholder="New York"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  State
                </label>
                <input
                  type="text"
                  value={form.state}
                  onChange={(e) => updateField('state', e.target.value)}
                  placeholder="NY"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Zip Code
                </label>
                <input
                  type="text"
                  value={form.zip_code}
                  onChange={(e) => updateField('zip_code', e.target.value)}
                  placeholder="10001"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Next button */}
            <button
              onClick={handleNext}
              className={`w-full flex items-center justify-center gap-2 py-3 border border-transparent text-sm font-medium rounded-full transition-colors ${
                isDetailsComplete
                  ? 'bg-primary-50/90 text-primary-600 hover:bg-white hover:border-primary-700'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              Next <RiArrowDownLine className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Operational Preferences Section */}
      <div className="bg-white rounded-2xl overflow-hidden">
        <button
          onClick={() => setActiveSection(activeSection === 'preferences' ? '' : 'preferences')}
          className="w-full flex items-center gap-3 p-5 text-left"
        >
          <RiListCheck3 className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-900">Operational Preferences</span>
        </button>

        {activeSection === 'preferences' && (
          <div className="px-5 pb-5 space-y-4">
            <p className="text-xs text-gray-400">
              Preferred operators, blacklisted aircraft models, and standard operational mandates (e.g., &quot;Always requires two pilots and a flight attendant&quot;)
            </p>
            <textarea
              value={form.operationalPreferences}
              onChange={(e) => updateField('operationalPreferences', e.target.value)}
              placeholder=""
              rows={6}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />

            {/* Back + Save buttons */}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setActiveSection('details')}
                className="flex-1 flex items-center justify-center gap-2 py-3 border border-gray-200 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-50 transition-colors"
              >
                <RiArrowUpLine className="w-4 h-4" />
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={saveMutation.isPending}
                className={`flex-[1.5] flex items-center justify-center gap-2 py-3 border border-transparent text-sm font-medium rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  form.operationalPreferences
                     ? 'bg-primary-50/90 text-primary-600 hover:bg-white hover:border-primary-700'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                <RiSaveLine className="w-4 h-4" />
                {saveMutation.isPending ? 'Saving...' : isEditMode ? 'Update Client' : 'Save Client'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddClient;
